import {DELETE, expectEither, HttpClient, HttpClient$, isAbortError, useHttpClient} from '@raison/network-facade';
import {invariant} from '@raison/toolbox';
import {CardId} from '@raison/types';
import {queryOptions, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {array} from 'io-ts';
import {atom} from 'jotai';
import {atomWithQuery} from 'jotai-tanstack-query';
import {GenericErrorMsg} from '~/entities/dialogs';
import {useAppDispatch} from '~/shared/store';
import {Card} from './CardSchema';

export const {queryKey: cardsQueryKey} = queryOptions<Card[]>({
    queryKey: ['cards'],
});

async function fetchCards(http: HttpClient) {
    const response = await http('/v1/cards').then(
        ...expectEither({
            200: array(Card),
        })
    );
    if (response.status === 'Abort') {
        throw response.reason;
    }
    return response.json;
}

export function useCardsQuery() {
    const http = useHttpClient();
    return useQuery({
        queryKey: cardsQueryKey,
        queryFn: () => fetchCards(http),
        refetchOnMount: true,
    });
}

const cardsQueryOptions$ = atom(get => ({
    queryKey: cardsQueryKey,
    queryFn: () => fetchCards(get(HttpClient$)),
    refetchOnMount: true,
}));

export const cardStatusQuery = (cardId: CardId) =>
    atomWithQuery(get => {
        const options = get(cardsQueryOptions$);
        return queryOptions({
            ...options,
            queryKey: [...options.queryKey, `${cardId}`],
            select: cards => {
                const card = cards.find(card => card.id === cardId);
                // FIXME: return Either and handle it in component by showing 404 page
                invariant(!!card, `Could not find card with id ${cardId}`);
                if (card.status === 'fraud') return 'not_verified';
                return card.status;
            },
        });
    });

export const useDeleteCard = () => {
    const http = useHttpClient();
    const queryClient = useQueryClient();
    const dispatch = useAppDispatch();

    return useMutation({
        mutationFn: async (id: CardId) => {
            await http(`/v1/cards/${id}`, DELETE());
        },
        onMutate: async id => {
            await queryClient.cancelQueries({queryKey: cardsQueryKey});
            const previousCards = queryClient.getQueryData(cardsQueryKey);
            queryClient.setQueryData(cardsQueryKey, old => old?.filter(card => id !== card.id));
            return {previousCards};
        },
        onError: (err, id, context) => {
            if (isAbortError(err)) {
                return;
            }
            if (context?.previousCards) {
                queryClient.setQueryData(cardsQueryKey, context?.previousCards);
            }
            dispatch(GenericErrorMsg.open());
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({queryKey: cardsQueryKey});
        },
    });
};
